/** La puerta. Existe para poder cerrar el demo: con `LLM=claude` y la URL abierta, cualquiera
 *  que tenga el link gasta dinero real a $0,05-0,15 por turno.
 *
 *  El token viaja al servidor y vuelve como cookie **HttpOnly**, así que ningún script de la
 *  página puede leerlo — por eso no se guarda en `localStorage`, que sería lo cómodo. El campo
 *  es `type="password"` para que no quede a la vista de nadie mirando por encima del hombro en
 *  una sala de reunión, que es exactamente donde se va a usar esto.
 */
import { useEffect, useState } from "react";
import { api, esNoAutorizado } from "../lib/api";
import { t } from "../lib/i18n";
import type { Idioma } from "../lib/tipos";
import { Logo } from "./Logo";

export function Login({ idioma, alIdioma, alEntrar }: {
  idioma: Idioma;
  alIdioma: (i: Idioma) => void;
  alEntrar: () => void;
}) {
  const [token, setToken] = useState("");
  const [estado, setEstado] = useState<"quieto" | "enviando" | "mal" | "caido">("quieto");
  // El descargo de datos sintéticos se AFIRMABA sin condición, y es lo primero que se lee antes
  // de entrar: en el servicio con dato real de Essity era sencillamente falso. Ahora solo se
  // pinta cuando el servidor lo ha dicho — y con dato real `/healthz` omite el campo a quien no
  // se ha identificado, así que aquí llega `undefined` y no se afirma NADA. Ausencia no es
  // afirmación: la misma regla que gobierna el campo en el servidor.
  const [sintetico, setSintetico] = useState<boolean | undefined>(undefined);
  const txt = t(idioma);

  useEffect(() => {
    api.salud().then((s) => setSintetico(s.sintetico)).catch(() => setSintetico(undefined));
  }, []);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!token.trim() || estado === "enviando") return;
    setEstado("enviando");
    try {
      await api.login(token.trim());
      alEntrar();
    } catch (err) {
      // Un token inválido y un servidor caído piden mensajes distintos: con uno solo, media hora
      // buscando el token correcto cuando el problema era la red.
      setEstado(esNoAutorizado(err) ? "mal" : "caido");
    }
  }

  return (
    <div className="acceso">
      <form className="acceso-caja" onSubmit={enviar}>
        <Logo alto={26} />
        <h1>{txt.accesoTitulo}</h1>
        <p className="acceso-intro">{txt.accesoIntro}</p>

        <label htmlFor="acceso-token">{txt.accesoEtiqueta}</label>
        <input
          id="acceso-token"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={token}
          onChange={(e) => { setToken(e.target.value); if (estado !== "enviando") setEstado("quieto"); }}
          aria-invalid={estado === "mal"}
          aria-describedby={estado === "mal" || estado === "caido" ? "acceso-error" : undefined}
        />

        {(estado === "mal" || estado === "caido") && (
          <p className="acceso-error" id="acceso-error" role="alert">
            {estado === "mal" ? txt.accesoMal : txt.accesoCaido}
          </p>
        )}

        <button type="submit" disabled={!token.trim() || estado === "enviando"}>
          {estado === "enviando" ? txt.accesoEntrando : txt.accesoEntrar}
        </button>

        {sintetico === true && <p className="acceso-sintetico">{txt.accesoSintetico}</p>}

        <div className="acceso-idioma" role="group" aria-label="idioma">
          {(["en", "es"] as const).map((i) => (
            <button key={i} type="button" className={i === idioma ? "activo" : undefined}
                    onClick={() => alIdioma(i)} aria-pressed={i === idioma}>
              {i.toUpperCase()}
            </button>
          ))}
        </div>
      </form>
    </div>
  );
}
